/**
 * Hero Highlight JavaScript
 * Handles fade-in animations for logo labels on page load
 */

class HeroHighlight {
  constructor() {
    this.activeUsersUpdateIntervalMs = 7000;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupAnimations());
    } else {
      this.setupAnimations();
    }
  }

  setupAnimations() {
    const heroSection = document.querySelector('.hero.highlight');
    if (!heroSection) return;

    this.setupActiveUsersCounter(heroSection);

    const overlay = heroSection.querySelector('.visual .overlay');
    if (!overlay) return;

    // Get all logo labels that need fade-in (only if overlay exists)
    const logoLabels = overlay.querySelectorAll('.is-tiktok, .is-kiyoh, .is-cbr');
    
    if (logoLabels.length === 0) return;

    // Add fade-in class after a short delay to trigger animation
    // This ensures the page has loaded before animation starts
    setTimeout(() => {
      logoLabels.forEach((label, index) => {
        // Stagger the animations slightly
        setTimeout(() => {
          label.classList.add('fade-in');
        }, index * 100); // 100ms delay between each label
      });
    }, 300); // Initial delay of 300ms after page load

    // Also handle intersection observer for when section comes into view
    this.setupIntersectionObserver(heroSection, logoLabels);
  }

  setupActiveUsersCounter(scopeElement) {
    const counterLabel = scopeElement.querySelector('[data-active-users]');
    if (!counterLabel) return;

    const countElement = counterLabel.querySelector('[data-active-users-count]');
    if (!countElement) return;

    const baseCount = Number.parseInt(counterLabel.dataset.baseCount || '', 10);
    if (Number.isNaN(baseCount) || baseCount < 1) return;

    const updateCount = () => {
      const fluctuatingCount = this.calculateFluctuatingCount(baseCount);
      countElement.textContent = fluctuatingCount.toLocaleString('nl-NL');
    };

    updateCount();
    window.setInterval(updateCount, this.activeUsersUpdateIntervalMs);
  }

  calculateFluctuatingCount(baseCount) {
    const now = new Date();
    const seconds = Date.now() / 1000;

    // Time-of-day: peak ~noon (163), low ~midnight (60). Use visitor's local time.
    const hourOfDay = now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;
    const dayPhase = Math.sin((hourOfDay - 6) / 12 * Math.PI); // -1 at night, 1 at noon
    const dayNightCount = 60 + (163 - 60) * (dayPhase + 1) / 2;

    // Add subtle micro-fluctuations on top for a live feel
    const microWave = Math.sin(seconds / 13 + baseCount * 0.03) * 4;
    const jitter = Math.cos(seconds / 7 + baseCount * 0.11) * 2;

    const adjustedCount = Math.round(dayNightCount + microWave + jitter);
    return Math.max(1, adjustedCount);
  }

  setupIntersectionObserver(heroSection, logoLabels) {
    // Use Intersection Observer to trigger animations when section is visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add is-inviewport class to visual element
            const visual = entry.target.querySelector('.visual');
            if (visual) {
              visual.classList.add('is-inviewport');
            }

            // Trigger fade-in for logo labels (only if they exist)
            if (logoLabels.length > 0) {
              logoLabels.forEach((label, index) => {
                setTimeout(() => {
                  label.classList.add('fade-in');
                }, index * 150); // Stagger animations
              });
            }

            // Unobserve after animation is triggered
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of section is visible
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before section is fully visible
      }
    );

    observer.observe(heroSection);
  }
}

// Initialize on page load
new HeroHighlight();
